import React from "react";
import { Instagram, Facebook } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { T, BRAND } from "@/content/site";
import { scrollToId } from "@/hooks/useLenis";

export default function Footer() {
  const { t } = useLang();
  const year = new Date().getFullYear();

  return (
    <footer data-testid="site-footer" className="relative bg-[#0A0A0A] border-t border-white/10 pt-24 pb-10">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        {/* Massive brand logo */}
        <div className="mb-16 md:mb-24 select-none flex justify-center md:justify-start">
          <img
            src="https://customer-assets-wrfwihn1.emergentagent.net/job_mezcal-luxury/artifacts/7kzlr9vd_LOGO%20SCRVM%20BLANCO.webp"
            alt="Mezcal SACRVM"
            className="w-full max-w-[1200px] h-auto"
            loading="lazy"
          />
        </div>

        <div className="grid grid-cols-12 gap-10 border-t border-white/10 pt-12">
          <div className="col-span-12 md:col-span-5">
            <div className="overline mb-4">{BRAND.fullName}</div>
            <p className="text-[color:var(--sacrvm-text-muted)] text-[15px] max-w-[380px] leading-[1.6]">
              {t(T.footer.tagline)}
            </p>
            <p className="mt-6 text-white/40 text-[12px] tracking-[0.24em] uppercase">
              {t(T.footer.responsibility)}
            </p>
          </div>

          <div className="col-span-6 md:col-span-3">
            <div className="overline mb-5">{t({ es: "Navegar", en: "Navigate" })}</div>
            <ul className="flex flex-col gap-3">
              {[
                { k: "story", id: "philosophy" },
                { k: "collection", id: "collection" },
                { k: "privateLabel", id: "private-label" },
                { k: "process", id: "process" },
                { k: "gallery", id: "gallery" },
                { k: "contact", id: "contact" },
              ].map((l) => (
                <li key={l.k}>
                  <button
                    onClick={() => scrollToId(l.id)}
                    className="text-white/80 hover:text-[color:var(--sacrvm-gold)] text-[14px] transition-colors"
                  >
                    {t(T.nav[l.k])}
                  </button>
                </li>
              ))}
              <li>
                <button onClick={() => scrollToId("gifting")} className="text-white/80 hover:text-[color:var(--sacrvm-gold)] text-[14px] transition-colors">
                  {t({ es: "Regalos Corporativos", en: "Corporate Gifting" })}
                </button>
              </li>
              <li>
                <button onClick={() => scrollToId("distributors")} className="text-white/80 hover:text-[color:var(--sacrvm-gold)] text-[14px] transition-colors">
                  {t({ es: "Distribuidores", en: "Distributors" })}
                </button>
              </li>
            </ul>
          </div>

          <div className="col-span-6 md:col-span-4">
            <div className="overline mb-5">{t({ es: "Contacto", en: "Contact" })}</div>
            <ul className="flex flex-col gap-3 text-[14px] text-white/80">
              <li><a href={`mailto:${BRAND.email}`} className="hover:text-[color:var(--sacrvm-gold)]">{BRAND.email}</a></li>
              <li><a href={`https://wa.me/${BRAND.whatsappNumber}`} target="_blank" rel="noreferrer" className="hover:text-[color:var(--sacrvm-gold)]">WhatsApp</a></li>
              <li>{BRAND.location}</li>
            </ul>
            <div className="mt-6 flex items-center gap-3">
              <a data-testid="footer-instagram" href={BRAND.socials.instagram} target="_blank" rel="noreferrer" className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-[color:var(--sacrvm-gold)] hover:text-[color:var(--sacrvm-gold)] transition-colors" aria-label="Instagram"><Instagram size={16} /></a>
              <a data-testid="footer-facebook" href={BRAND.socials.facebook} target="_blank" rel="noreferrer" className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-[color:var(--sacrvm-gold)] hover:text-[color:var(--sacrvm-gold)] transition-colors" aria-label="Facebook"><Facebook size={16} /></a>
            </div>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-[11px] tracking-[0.24em] uppercase text-white/40">
          <div>© {year} SACRVM. {t(T.footer.rights)}</div>
          <div className="flex items-center gap-6">
            <a href="#" className="hover:text-white transition-colors">{t(T.footer.privacy)}</a>
            <span>Hecho en Oaxaca</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
